if (!customElements.get('media-kit-file')) {
  customElements.define(
    'media-kit-file',
    class MediaKitFile extends HTMLElement {
      static EOCD_SIGNATURE = 0x06054b50;
      static CD_SIGNATURE = 0x02014b50;
      static TAIL_SIZE = 65557; // max EOCD (22) + max comment (65535)
      static IGNORED = /(^|\/)(__MACOSX\/|\.DS_Store$|Thumbs\.db$|desktop\.ini$)/i;

      connectedCallback() {
        this.details = this.closest('details');

        if (!this.details || this.details.open) {
          this.load();
        }
        else {
          this.onToggle = () => this.details.open && this.load();
          this.details.addEventListener('toggle', this.onToggle);
          // Prefetch on hover so the details are ready when the item opens.
          this.details.querySelector('summary')?.addEventListener('pointerenter', this.load.bind(this), { once: true });
        }
      }

      disconnectedCallback() {
        if (this.onToggle) this.details.removeEventListener('toggle', this.onToggle);
      }

      get url() {
        // Fetch from the store's own domain to avoid CORS on cdn.shopify.com.
        const url = new URL(this.dataset.url, window.location.href);
        const match = url.pathname.match(/^\/s\/files\/(?:\d+\/)+files\/(.+)$/);
        if (url.hostname === 'cdn.shopify.com' && match) {
          return `/cdn/shop/files/${match[1]}${url.search}`;
        }
        if (url.pathname.startsWith('/cdn/shop/')) {
          return `${url.pathname}${url.search}`;
        }
        return url.href;
      }

      get isZip() {
        return this.dataset.extension === 'zip';
      }

      async load() {
        if (this.loaded) return;
        this.loaded = true;

        const cacheKey = `media-kit:${this.dataset.url}`;
        let info = null;

        try {
          info = JSON.parse(sessionStorage.getItem(cacheKey));
        } catch (error) {}

        if (!info) {
          try {
            info = await this.inspect();
          } catch (error) {
            console.warn('[media-kit] Could not read file details', this.dataset.url, error);
            return;
          }

          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(info));
          } catch (error) {}
        }

        this.render(info);
      }

      async inspect() {
        const url = this.url;
        const info = {};
        const range = this.isZip ? `bytes=-${MediaKitFile.TAIL_SIZE}` : 'bytes=0-0';
        const controller = new AbortController();
        const response = await fetch(url, { headers: { Range: range }, signal: controller.signal });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        // Shopify Files links carry the upload time as ?v=<unix seconds>.
        const version = parseInt(this.dataset.version, 10);
        const lastModified = response.headers.get('Last-Modified');
        // Only trust ?v= when it looks like a unix timestamp (2001–2286); file_url uses other values.
        if (version > 1e9 && version < 1e10) info.updated = version * 1000;
        else if (lastModified) info.updated = lastModified;

        if (response.status === 206) {
          const total = (response.headers.get('Content-Range') || '').split('/')[1];
          if (total && total !== '*') info.size = parseInt(total, 10);
        }
        else {
          // Range ignored: the full file is coming. Take the size and stop downloading.
          const length = response.headers.get('Content-Length');
          if (length) info.size = parseInt(length, 10);
          controller.abort();
          return info;
        }

        if (this.isZip) {
          const tail = new DataView(await response.arrayBuffer());
          Object.assign(info, await this.readZip(url, tail, info.size));
        }

        return info;
      }

      async readZip(url, tail, size) {
        let eocd = -1;
        for (let i = tail.byteLength - 22; i >= 0; i--) {
          if (tail.getUint32(i, true) === MediaKitFile.EOCD_SIGNATURE) {
            eocd = i;
            break;
          }
        }
        if (eocd < 0) return {};

        const entries = tail.getUint16(eocd + 10, true);
        const cdSize = tail.getUint32(eocd + 12, true);
        const cdOffset = tail.getUint32(eocd + 16, true);

        // ZIP64 archives: skip the details rather than report wrong numbers.
        if (entries === 0xffff || cdOffset === 0xffffffff) return {};

        let directory;
        const tailStart = (size || tail.byteLength) - tail.byteLength;
        if (cdOffset >= tailStart) {
          directory = new DataView(tail.buffer, cdOffset - tailStart, cdSize);
        }
        else {
          const response = await fetch(url, { headers: { Range: `bytes=${cdOffset}-${cdOffset + cdSize - 1}` } });
          if (response.status !== 206) return { count: entries };
          directory = new DataView(await response.arrayBuffer());
        }

        const decoder = new TextDecoder();
        const extensions = new Set();
        let count = 0;
        let offset = 0;
        let newest = 0; // newest entry date as YYYYMMDD

        while (offset + 46 <= directory.byteLength && directory.getUint32(offset, true) === MediaKitFile.CD_SIGNATURE) {
          const nameLength = directory.getUint16(offset + 28, true);
          const extraLength = directory.getUint16(offset + 30, true);
          const commentLength = directory.getUint16(offset + 32, true);
          const dosDate = directory.getUint16(offset + 14, true);
          const name = decoder.decode(new Uint8Array(directory.buffer, directory.byteOffset + offset + 46, nameLength));
          offset += 46 + nameLength + extraLength + commentLength;

          if (name.endsWith('/') || MediaKitFile.IGNORED.test(name)) continue;

          count++;
          const date = (((dosDate >> 9) & 0x7f) + 1980) * 10000 + ((dosDate >> 5) & 0x0f) * 100 + (dosDate & 0x1f);
          if (date > newest) newest = date;
          const base = name.split('/').pop();
          if (base.includes('.')) extensions.add(`.${base.split('.').pop().toLowerCase()}`);
        }

        const info = { count, extensions: [...extensions].sort() };
        if (newest) {
          const date = String(newest);
          info.updated = `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T12:00:00`;
        }
        return info;
      }

      render(info) {
        if (info.size) {
          this.fill('size', this.formatSize(info.size));
        }

        const updated = this.querySelector('[data-updated]');
        if (info.updated && updated && !updated.hasAttribute('data-manual')) {
          const date = new Date(info.updated);
          if (!isNaN(date)) {
            this.fill('updated', date.toLocaleDateString(document.documentElement.lang || undefined, {
              day: '2-digit', month: '2-digit', year: 'numeric'
            }));
          }
        }

        const countElement = this.querySelector('[data-count]');
        if (info.count && !countElement.hasAttribute('data-manual')) {
          const template = info.count === 1 ? this.dataset.countOne : this.dataset.countOther;
          countElement.textContent = template.replace('999999', info.count);
          countElement.hidden = false;
        }

        if (info.extensions?.length && !this.querySelector('[data-formats]').hasAttribute('data-manual')) {
          this.fill('formats', info.extensions.join(' | '));
        }
      }

      fill(name, value) {
        this.querySelector(`[data-${name}-value]`).textContent = value;
        this.querySelector(`[data-${name}]`).hidden = false;
      }

      formatSize(bytes) {
        const units = ['B', 'KB', 'MB', 'GB'];
        let value = bytes;
        let unit = 0;
        while (value >= 1024 && unit < units.length - 1) {
          value /= 1024;
          unit++;
        }
        return `${unit === 0 ? value : value.toFixed(2)} ${units[unit]}`;
      }
    }
  );
}
